import React, { useMemo } from 'react';
import { ReactFlowProvider } from 'reactflow';
import Controller, { ControllerProps } from './Controller';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ToastProvider } from './contexts/ToastContext';
import { parseWorkflowProject } from './utils/pssHelper';
import { WorkflowProject, ProcessDefinition } from './types';
import './styles/index.css';

// Re-export types for consuming applications
export * from './types';
export type { ControllerProps } from './Controller';

/**
 * Check if the data is a WorkflowProject (PSS format) vs ProcessDefinition[]
 */
const isWorkflowProject = (data: unknown): data is WorkflowProject => {
  return (
    typeof data === 'object' &&
    data !== null &&
    'Processes' in data &&
    Array.isArray((data as WorkflowProject).Processes)
  );
};

/**
 * Wrapper component for the Workflow Editor that provides necessary contexts.
 * This is the main entry point for the CDN bundle.
 *
 * Accepts initialData as either:
 * - ProcessDefinition[] (editor format)
 * - WorkflowProject (PSS format) - will be automatically parsed
 */
export const WorkflowEditorWrapper: React.FC<ControllerProps & { initialData?: ProcessDefinition[] | WorkflowProject }> = (props) => {
  const { initialData, ...restProps } = props;

  // Parse initialData if it's in WorkflowProject (PSS) format
  const parsedInitialData = useMemo(() => {
    if (!initialData) return undefined;
    if (isWorkflowProject(initialData)) {
      return parseWorkflowProject(initialData);
    }
    return initialData;
  }, [initialData]);

  return (
    <ErrorBoundary>
      <ToastProvider>
        <ReactFlowProvider>
          <div style={{ width: '100%', height: '100%' }}>
            <Controller {...restProps} initialData={parsedInitialData} />
          </div>
        </ReactFlowProvider>
      </ToastProvider>
    </ErrorBoundary>
  );
};

export default WorkflowEditorWrapper;
